import React, { useEffect, useState } from "react";
import TopNav from "../layouts/TopNav";
import "../Styles/invoiceTable.css";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate, Link } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import ReLoginModal from "../components/ReLoginModal";
import { CircularProgress } from "@mui/material";
import api from "../util/axios";

type Customer = {
  _id: string;
  businessName: string;
  phone: string;
};

type Requisition = {
  _id: string;
  approvalNumber: string;
  customer: Customer | null;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  quantityToLoad: number;
};

type LoadingSlip = {
  _id: string;
  loadingNumber: string;
  truckNumber: string;
  supplyLocation: string;
  requisitions: Requisition[];
  createdAt: string;
  updatedAt: string;
  driver: string;
};

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const NotInvoicedTable = () => {
  const navigate = useNavigate();
  const [reLogin, setReLogin] = useState(false);
  const [loadingSlips, setLoadingSlips] = useState<LoadingSlip[]>([]);
  const [selectedSlips, setSelectedSlips] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("businessName");
  const [businessQuery, setBusinessQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [truckQuery, setTruckQuery] = useState("");

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setIsAdmin(user.account?.role === "admin");
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const fetchNotInvoicedData = async () => {
    setIsLoading(true);
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      setIsLoading(false);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await api.get(
        "/loading-slip?invoiced=false",
        { headers }
      );
      setLoadingSlips(response.data.data);
      // console.log("Fetched loading slips:", response.data.data);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch slips";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
         
        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error("Session expired!. Login again");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReturnedInvoices = async () => {
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    try {
      const response = await api.get(
        `/loading-slip/returns/quantity`,
        { headers }
      );
      const returnedInvoices = response.data.data;
      // console.log("Returned Invoices:", returnedInvoices);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch returned invoices";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
         
        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    fetchNotInvoicedData();
    fetchReturnedInvoices();
  }, []);

  const filterByDate = (slips: LoadingSlip[]) => {
    if (!fromDate && !toDate) return slips;

    return slips.filter((slip) => {
      const slipDate = new Date(slip.createdAt);
      const startDate = fromDate ? new Date(fromDate) : null;
      const endDate = toDate ? new Date(toDate) : null;

      if (endDate) {
        endDate.setHours(23, 59, 59);
      }

      return (
        (!startDate || slipDate >= startDate) &&
        (!endDate || slipDate <= endDate)
      );
    });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateInvoice = (data: LoadingSlip) => {
    navigate("/dashboard/generatedslipNi", { state: data });
  };

  const handleReturn = async (slip: LoadingSlip) => {
    const token = window.localStorage.getItem("token");
    if (!token) {
      setReLogin(true);
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    console.log("Returning slip:", slip);
    if (slip.requisitions.some((req) => req.quantityToLoad === 0)) {
      toast.error("Cannot return slip with zero quantity to load.");
      return;
    }

    try {
      await api.patch(
        `/loading-slip/${slip._id}/return`,
        {},
        { headers }
      );
      setLoadingSlips((prev) => prev.filter((item) => item._id !== slip._id));
      toast.success("Quantity returned successfully");
    } catch (error) {
      const err = error as ApiError;
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to return slip";
      if (
        errorMessage.includes("jwt expired") ||
        errorMessage.includes("jwt malformed")
      ) {
         
        setReLogin(true);
      } else {
        setReLogin(false);
      }
      toast.error(errorMessage);
    }
  };

  const handleSelectSlip = (id: string) => {
    setSelectedSlips((prev) =>
      prev.includes(id) ? prev.filter((slipId) => slipId !== id) : [...prev, id]
    );
  };

  const handleMergeSlips = () => {
    if (selectedSlips.length === 0) {
      toast.error("Please select at least one loading slip to merge.");
      return;
    } else if (selectedSlips.length < 2) {
      toast.error("Please select at least two loading slips to merge.");
      return;
    }

    const selectedData = loadingSlips.filter((slip) =>
      selectedSlips.includes(slip._id)
    );

    const mergedSlips = {
      selectedData,
      slipIds: selectedSlips,
      approvalNumbers: selectedData
        .map((slip) =>
          slip.requisitions.map((req) => req.approvalNumber).join(", ")
        )
        .join(" / "),
    };
    navigate("/dashboard/merged-generated-slips", { state: mergedSlips });
  };

  const filteredSlips = filterByDate(loadingSlips).filter((slip) => {
    if (searchField !== "combined" && !searchQuery) return true;
    if (
      searchField === "combined" &&
      !businessQuery &&
      !productQuery &&
      !truckQuery
    )
      return true;

    const requisitions = slip.requisitions;
    const truckNumber = slip.truckNumber?.toLowerCase();
    const businessName = requisitions[0]?.customer?.businessName?.toLowerCase();
    const itemName = requisitions[0]?.itemName?.toLowerCase();

    const businessNameMatch =
      searchField === "combined"
        ? businessName?.includes(businessQuery.toLowerCase())
        : businessName?.includes(searchQuery.toLowerCase());
    const itemNameMatch =
      searchField === "combined"
        ? itemName?.includes(productQuery.toLowerCase())
        : itemName?.includes(searchQuery.toLowerCase());
    const truckNumberMatch =
      searchField === "combined"
        ? truckNumber?.includes(truckQuery.toLowerCase())
        : truckNumber?.includes(searchQuery.toLowerCase());

    switch (searchField) {
      case "businessName":
        return businessNameMatch;
      case "productName":
        return itemNameMatch;
      case "truckNumber":
        return truckNumberMatch;
      case "combined":
        return businessNameMatch && itemNameMatch && truckNumberMatch;
      default:
        return businessNameMatch;
    }
  });

  return (
    <div className="board">
      <ToastContainer />
      <TopNav title="Not Invoiced Loading Slip" />
      {reLogin ? <ReLoginModal /> : null}
      <div
        className="method"
        style={{ justifyContent: "flex-end", marginTop: "15px" }}
      >
        <div className="filterBy">
          <strong>Filter by: </strong>
          <span>Start date: </span>
          <input
            type="date"
            name="startDate"
            id=""
            onChange={(e) => setFromDate(e.target.value)} />
          <span>End date: </span>
          <input
            type="date"
            name="endDate"
            id=""
            onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="searchFieldSelect">
          <label htmlFor="filter">Search filter: </label>
          <select
            id="filter"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
          >
            <option value="businessName">Customer Name</option>
            <option value="productName">Item Name</option>
            <option value="truckNumber">Truck Number</option>
            <option value="combined">Mixed search</option>
          </select>
        </div>
        {searchField === "combined" ? (
          <div className="combinedSearch">
            <input
              type="text"
              placeholder="Customer Name"
              value={businessQuery}
              onChange={(e) => setBusinessQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Item Name"
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
            />
            <input
              type="text"
              placeholder="Truck Number"
              value={truckQuery}
              onChange={(e) => setTruckQuery(e.target.value)}
            />
          </div>
        ) : (
          <div className="apInput">
            <input
              type="text"
              value={searchQuery}
              placeholder="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <BsSearch className="apIcon" />
          </div>
        )}
      </div>
      <div
        className="legend"
        style={{ marginTop: "10px", fontSize: "0.9em", color: "#555" }}
      >
        <p>
          <span className="merged-badge">Merged</span> indicates loading slips
          with multiple requisitions, highlighted in yellow.
          <span className="returned-badge">Returned</span> indicates loading
          slips that have been returned, highlighted in red.
        </p>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "2em",
        }}
      >
        <button
          onClick={handleMergeSlips}
          disabled={selectedSlips.length === 0}
          style={{
            margin: "10px",
            padding: "8px 16px",
            opacity: selectedSlips.length === 0 ? 0.5 : 1,
            cursor: selectedSlips.length === 0 ? "not-allowed" : "pointer",
            borderRadius: "6px",
            background: "#007bff",
            border: "none",
            color: "#fff",
            fontSize: "13px",
          }}
        >
          Merge & print Loading Slip
        </button>
        <Link to="/dashboard/invoicetable">
          <button
            style={{
              width: "120px",
              height: "30px",
              flexShrink: "0",
              borderRadius: "6px",
              background: "#007bff",
              border: "none",
              color: "#fff",
              fontSize: "13px",
              fontStyle: "normal",
              fontWeight: "500",
              lineHeight: "normal",
              cursor: "pointer",
            }}
          >
            Invoiced
          </button>
        </Link>
      </div>
      <div className="invoiceTable">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CircularProgress />
            <h2>Please wait while we process the request...</h2>
          </div>
        ) : (
          <table className="inTable">
            <thead>
              <tr className="heTable">
                <th>
                  <input
                    type="checkbox"
                    checked={
                      selectedSlips.length === filteredSlips.length &&
                      filteredSlips.length > 0
                    }
                    onChange={() =>
                      setSelectedSlips(
                        selectedSlips.length === filteredSlips.length
                          ? []
                          : filteredSlips.map((slip) => slip._id)
                      )
                    }
                  />
                </th>
                <th>Customer Name</th>
                <th>Item Name</th>
                <th>Unit Price</th>
                <th>Truck Number</th>
                <th>Quantity to Load</th>
                <th>Total Cost</th>
                <th>Date</th>
                <th>Continue</th>
                <th>Return</th>
              </tr>
            </thead>
            <tbody>
              {filteredSlips.length > 0 ? (
                filteredSlips
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((slip) => {
                    const requisitions = slip.requisitions;
                    const isMerged = requisitions.length > 1;
                    const isReturned = requisitions[0]?.quantityToLoad === 0;
                    const totalQuantityToLoad = requisitions.reduce(
                      (acc, req) => acc + req.quantityToLoad,
                      0
                    );
                    const totalCost = requisitions.reduce(
                      (acc, req) => acc + req.totalCost,
                      0
                    );
                    const formattedDate = new Date(
                      slip.updatedAt
                    ).toLocaleDateString();

                    return (
                      <tr
                        key={slip._id}
                        className={`taData ${isMerged ? "merged" : ""}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedSlips.includes(slip._id)}
                            onChange={() => handleSelectSlip(slip._id)}
                          />
                        </td>
                        <td>
                          {requisitions[0]?.customer?.businessName || "N/A"}
                          {isMerged && (
                            <span className="merged-badge">Merged</span>
                          )}
                          {isReturned && (
                            <span className="returned-badge">Returned</span>
                          )}
                        </td>
                        <td>{requisitions[0]?.itemName || "N/A"}</td>
                        <td>{requisitions[0]?.unitPrice || "N/A"}</td>
                        <td>{slip.truckNumber || "N/A"}</td>
                        <td>{totalQuantityToLoad}</td>
                        <td>{totalCost}</td>
                        <td>{formattedDate}</td>
                        <td>
                          <button
                            className="action-btn"
                            onClick={() => handleCreateInvoice(slip)}
                          >
                            Continue Loading Slip
                          </button>
                        </td>
                        <td>
                          {isAdmin ? (
                            <button
                              className="action-btn"
                              onClick={() => handleReturn(slip)}
                            >
                              Return
                            </button>
                          ) : (
                            <button
                              className="action-btn"
                              disabled
                              style={{ cursor: "not-allowed", opacity: 0.5 }}
                            >
                              Return
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <TablePagination
        rowsPerPageOptions={[15, 25, 50]}
        component="div"
        count={filteredSlips.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default NotInvoicedTable;